import { useApolloClient, useMutation } from "@apollo/client/react"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import { MutationSchema } from "shared/api/types"
import { Logger } from "shared/lib/Logger"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { generateUUID } from "shared/lib/generateUUID"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApiResponse } from "shared/types/ApiResponse"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import {
  NEW_PROJECT_COLOR,
  Project,
  ProjectTypename,
  ProjectUITypename
} from "shared/types/Project"
import { ProjectImportStatus } from "shared/types/ProjectImport"
import {
  CREATE_PROJECT,
  IMPORT_XLSX_PROJECT
} from "shared/types/ProjectMutations"
import { ProjectImportError } from "shared/types/__generated__/graphql"
import {
  CreateProjectImportInputSchema,
  ProjectImportSchema
} from "shared/types/__generated__/validation"

import {
  CreateProjectImportDocument,
  CreateProjectImportMutation,
  CreateProjectImportMutationVariables
} from "entities/projects/graphql/import-mutations.generated"
import {
  useProjectChildrenLazyQuery,
  useProjectsLazyQuery
} from "entities/projects/graphql/queries.generated"
import { CreateProjectTransformSchema } from "entities/projects/lib/CreateProjectTransformSchema"
import { createProjectInCache } from "entities/projects/lib/createProjectInCache"
import { evictProjectAndSort } from "entities/projects/lib/evictProjectAndSort"
import { useProjectsActions } from "entities/projects/model/store/useProjectsActions"
import { z } from "zod"

import { CreateableProject } from "./useCreateProject"
import { useImportStatus } from "./useImportStatus"

const importMutationSchemas = {
  input: z.object({ input: CreateProjectImportInputSchema() }),
  response: MutationSchema.extend({
    projectImport: ProjectImportSchema()
  })
}

function buildOptimisticProject(project: CreateableProject) {
  const id = project.id || generateUUID()
  const parsed = CreateProjectTransformSchema.safeParse({
    ...project,
    id,
    __typename: ProjectTypename,
    ui: { color: NEW_PROJECT_COLOR, __typename: ProjectUITypename }
  })
  return { id, optimisticProject: parsed.success ? parsed.data : null }
}

function buildImportInput(project: CreateableProject, s3Key: string) {
  return {
    id: generateUUID(),
    s3Key,
    projectFields: {
      id: project.id || generateUUID(),
      title: project.title,
      phaseId: project.phase?.id
    }
  }
}

function toImportErrorResponse(
  errors: ProjectImportError[] | null | undefined
) {
  const importErrors: ProjectImportError[] = errors?.length
    ? errors
    : [
        {
          __typename: "ProjectImportError",
          errors: ["Import failed"],
          isValid: false
        }
      ]
  return {
    data: null,
    errors: importErrors.map(e => ({
      message: e.errors?.join(", ") ?? "Import Failed!",
      data: e
    }))
  }
}

export function useCreateProjectFromXlsx() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const { setProjectPending, unsetProjectPending } = useProjectsActions()
  const startProjectRequest = useProjectRequestMonitor()
  const [fetchProjects] = useProjectsLazyQuery()
  const [fetchProjectChildren] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Project.IMPORT_XLSX_PROJECT.Start"),
      success: t("Request.Project.IMPORT_XLSX_PROJECT.Success"),
      failure: t("Request.Project.IMPORT_XLSX_PROJECT.Failure")
    }
  })

  const [createImportMutate] = useMutation<
    CreateProjectImportMutation,
    CreateProjectImportMutationVariables
  >(CreateProjectImportDocument)

  const handleImportStatus = useImportStatus()

  const handleImportComplete = useEvent(
    (projectId: string) => async (): Promise<Project | null> => {
      const result = await fetchProjectChildren({
        variables: { id: projectId },
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
      })
      return result.data?.project ?? null
    }
  )

  const createProjectFromXlsx = useEvent(
    async ({
      project,
      s3Key
    }: {
      project: CreateableProject
      s3Key: string
    }): Promise<
      ApiResponse<
        { project: Promise<ApiResponse<Project>> },
        Record<string, unknown>,
        ProjectImportError
      >
    > => {
      const { id, optimisticProject } = buildOptimisticProject(project)

      if (!optimisticProject) {
        Logger.error(
          "Invalid project input for XLSX import",
          "useCreateProjectFromXlsx",
          project
        )
        return {
          data: null,
          errors: [{ message: "Invalid project input for XLSX import" }]
        }
      }

      const importResult = await callMutation(createImportMutate, {
        variables: { input: buildImportInput(project, s3Key) },
        transform: data => data.projectImport,
        schemas: importMutationSchemas
      })

      if (!importResult.data) {
        return {
          data: null,
          errors: [{ message: "Import creation returned no data" }]
        }
      }

      const importErrors = importResult.data.error ?? []
      if (importResult.data.status === ProjectImportStatus.ERROR) {
        return toImportErrorResponse(importErrors)
      }

      setProjectPending({ projectID: id })
      createProjectInCache({
        cache: apolloCache,
        project: optimisticProject,
        writeChildrenQuery: false
      })

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID: id,
        requestType: IMPORT_XLSX_PROJECT
      })

      emitAppEvent(CREATE_PROJECT, { id })

      const projectPromise: Promise<ApiResponse<Project>> = handleImportStatus({
        importId: importResult.data.id,
        onComplete: handleImportComplete(id)
      })
        .then(async result => {
          await fetchProjects()
          raiseSuccessToast()
          return result
            ? { data: result }
            : { data: null, errors: [{ message: "Project not found" }] }
        })
        .catch(error => {
          Logger.error(error, "useCreateProjectFromXlsx", { id, s3Key })
          evictProjectAndSort(id, apolloCache)
          fetchProjects()
          raiseErrorToast()
          return { data: null, errors: [{ message: "Project import failed" }] }
        })
        .finally(() => {
          completeProjectRequest()
          unsetProjectPending({ projectID: id })
        })

      return { data: { project: projectPromise } }
    }
  )

  return { createProjectFromXlsx }
}
